/**
 * AuditLogSearchSection — Issue #287.
 *
 * Renders the admin-side audit-log search UI inside the `/settings` page,
 * replacing the `settings-missing-audit_log_search` placeholder for users
 * holding `audit:read` (or `*:*`).
 *
 * Red lines:
 *   - Permission gate is component-level: when the caller lacks
 *     `audit:read` / `*:*` we render denial copy WITHOUT mounting
 *     {@link AuthorizedAuditLogSearch}. The audit-log hook is therefore
 *     never instantiated and `/api/v1/audit-logs` is never called for
 *     unauthorized accounts.
 *   - The table renders only the sanitized fields returned by
 *     `AuditLogEntryDto`. Raw payloads, complete tenant/org/resource/target
 *     ids, phone numbers, ID cards, IPs, user agents, tokens, cookies,
 *     production env values, or secrets are NEVER rendered here.
 *   - The export task list is read-only. Export creation requires a
 *     dedicated second-factor workflow; we deliberately do not surface a
 *     "create export" form, and we never POST `secondFactorResult=passed`.
 *   - The frontend warns when `actorId` equals the current user (backend
 *     still rejects self-audit search authoritatively).
 */
import { useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { ClipboardList, Database, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DEFAULT_AUDIT_LOG_PAGE_SIZE,
  useAuditLogs,
  type AuditLogEntryDto,
  type AuditLogExportDto,
  type AuditLogSearchFilters,
  type AsyncState,
  type AuditLogListResponse,
  type AuditLogSummaryResponse,
} from '@/hooks/useAuditLogs';
import {
  AUDIT_READ_PERMISSION,
  canReadAuditLogExports,
  canReadAuditLogs,
} from '@/lib/auditLogPermissions';

const STATUS_OPTIONS: ReadonlyArray<{ value: '' | 'success' | 'failure'; label: string }> = [
  { value: '', label: '不限' },
  { value: 'success', label: '成功' },
  { value: 'failure', label: '失败' },
];

const RISK_OPTIONS: ReadonlyArray<{
  value: '' | 'low' | 'normal' | 'high' | 'critical';
  label: string;
}> = [
  { value: '', label: '不限' },
  { value: 'low', label: '低' },
  { value: 'normal', label: '普通' },
  { value: 'high', label: '高' },
  { value: 'critical', label: '紧急' },
];

const EXPORT_STATUS_LABEL: Record<AuditLogExportDto['status'], string> = {
  pending: '待处理',
  running: '生成中',
  succeeded: '已完成',
  failed: '失败',
  expired: '已过期',
  cancelled: '已取消',
};

const RAW_PAYLOAD_VISIBILITY_LABEL: Record<
  AuditLogEntryDto['rawPayloadVisibility'],
  string
> = {
  hidden: '不可见',
  summary_only: '仅摘要',
  reveal_required: '需 reveal',
};

const RISK_LEVEL_LABEL: Record<AuditLogEntryDto['riskLevel'], string> = {
  low: '低',
  normal: '普通',
  high: '高',
  critical: '紧急',
};

interface FilterFormState {
  from: string;
  to: string;
  action: string;
  resource: string;
  status: '' | 'success' | 'failure';
  eventType: string;
  eventCodePrefix: string;
  riskLevel: '' | 'low' | 'normal' | 'high' | 'critical';
  targetType: string;
  targetId: string;
  approvalNo: string;
  actorId: string;
}

const EMPTY_FORM: FilterFormState = {
  from: '',
  to: '',
  action: '',
  resource: '',
  status: '',
  eventType: '',
  eventCodePrefix: '',
  riskLevel: '',
  targetType: '',
  targetId: '',
  approvalNo: '',
  actorId: '',
};

export interface AuditLogSearchSectionProps {
  readonly permissions: readonly string[];
  readonly currentUserId: string | null;
}

/**
 * Permission gate. Splits the hook into a child component so that
 * `useAuditLogs` is never instantiated for unauthorized callers, which
 * keeps `/api/v1/audit-logs` requests out of the network tab for accounts
 * without `audit:read` / `*:*`.
 */
export default function AuditLogSearchSection({
  permissions,
  currentUserId,
}: AuditLogSearchSectionProps): ReactElement | null {
  const canRead = canReadAuditLogs(permissions);
  if (!canRead) {
    return (
      <section
        className="space-y-3"
        data-testid="settings-audit-log-search-section"
        aria-label="审计日志查询"
      >
        <header>
          <h3 className="text-lg font-medium">审计日志查询</h3>
          <p
            className="text-sm text-muted-foreground"
            data-testid="settings-audit-log-search-denied"
          >
            当前账号未获得 <code>{AUDIT_READ_PERMISSION}</code> 权限；请联系超级管理员授权后再查看。
          </p>
        </header>
      </section>
    );
  }
  return (
    <AuthorizedAuditLogSearch
      permissions={permissions}
      currentUserId={currentUserId}
    />
  );
}

function AuthorizedAuditLogSearch({
  permissions,
  currentUserId,
}: AuditLogSearchSectionProps): ReactElement {
  const view = useAuditLogs({ permissions });
  const [form, setForm] = useState<FilterFormState>(EMPTY_FORM);
  const showExports = canReadAuditLogExports(permissions);

  const formIds = {
    from: useId(),
    to: useId(),
    action: useId(),
    resource: useId(),
    status: useId(),
    eventType: useId(),
    eventCodePrefix: useId(),
    riskLevel: useId(),
    targetType: useId(),
    targetId: useId(),
    approvalNo: useId(),
    actorId: useId(),
  };

  const actorWarn =
    form.actorId.trim().length > 0 &&
    currentUserId !== null &&
    form.actorId.trim() === currentUserId;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const next: AuditLogSearchFilters = {
      ...(form.from ? { from: form.from } : {}),
      ...(form.to ? { to: form.to } : {}),
      ...(form.action ? { action: form.action } : {}),
      ...(form.resource ? { resource: form.resource } : {}),
      ...(form.status ? { status: form.status } : {}),
      ...(form.eventType ? { eventType: form.eventType } : {}),
      ...(form.eventCodePrefix
        ? { eventCodePrefix: form.eventCodePrefix }
        : {}),
      ...(form.riskLevel ? { riskLevel: form.riskLevel } : {}),
      ...(form.targetType ? { targetType: form.targetType } : {}),
      ...(form.targetId ? { targetId: form.targetId } : {}),
      ...(form.approvalNo ? { approvalNo: form.approvalNo } : {}),
      ...(form.actorId ? { actorId: form.actorId } : {}),
    };
    view.applyFilters(next);
  };

  const handleReset = (): void => {
    setForm(EMPTY_FORM);
    view.resetFilters();
  };

  return (
    <section
      className="space-y-3"
      data-testid="settings-audit-log-search-section"
      aria-label="审计日志查询"
    >
      <header>
        <h3 className="text-lg font-medium">审计日志查询</h3>
        <p className="text-sm text-muted-foreground">
          搜索基于 GET <code>/api/v1/audit-logs</code> 与{' '}
          <code>/api/v1/audit-logs/summary</code>。返回字段全部为后端脱敏摘要：
          租户/机构/资源/目标 ID 仅为掩码，IP/UA 仅为摘要；原始请求/响应永不下发。
          员工不可查询自己的操作审计（后端权威拒绝）。
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" aria-hidden />
            过滤条件
          </CardTitle>
          <CardDescription>
            日期窗口最大 92 天；单页最多 {view.limit} 条；后端拒绝越权字段。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 md:grid-cols-3"
            onSubmit={handleSubmit}
            data-testid="settings-audit-log-search-form"
          >
            <div className="space-y-1">
              <Label htmlFor={formIds.from}>起始时间</Label>
              <Input
                id={formIds.from}
                type="datetime-local"
                value={form.from}
                onChange={(e) => setForm((s) => ({ ...s, from: e.target.value }))}
                data-testid="settings-audit-log-search-from"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.to}>结束时间</Label>
              <Input
                id={formIds.to}
                type="datetime-local"
                value={form.to}
                onChange={(e) => setForm((s) => ({ ...s, to: e.target.value }))}
                data-testid="settings-audit-log-search-to"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.status}>状态</Label>
              <select
                id={formIds.status}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={form.status}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    status: e.target.value as FilterFormState['status'],
                  }))
                }
                data-testid="settings-audit-log-search-status"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value || 'any'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.action}>动作字典</Label>
              <Input
                id={formIds.action}
                value={form.action}
                onChange={(e) => setForm((s) => ({ ...s, action: e.target.value }))}
                placeholder="例如 customers.update"
                data-testid="settings-audit-log-search-action"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.resource}>资源</Label>
              <Input
                id={formIds.resource}
                value={form.resource}
                onChange={(e) => setForm((s) => ({ ...s, resource: e.target.value }))}
                placeholder="例如 customers"
                data-testid="settings-audit-log-search-resource"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.eventType}>事件分类</Label>
              <Input
                id={formIds.eventType}
                value={form.eventType}
                onChange={(e) =>
                  setForm((s) => ({ ...s, eventType: e.target.value }))
                }
                placeholder="例如 auth"
                data-testid="settings-audit-log-search-event-type"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.eventCodePrefix}>事件编码前缀</Label>
              <Input
                id={formIds.eventCodePrefix}
                value={form.eventCodePrefix}
                onChange={(e) =>
                  setForm((s) => ({ ...s, eventCodePrefix: e.target.value }))
                }
                placeholder="例如 customers."
                data-testid="settings-audit-log-search-event-code-prefix"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.riskLevel}>风险等级</Label>
              <select
                id={formIds.riskLevel}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={form.riskLevel}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    riskLevel: e.target.value as FilterFormState['riskLevel'],
                  }))
                }
                data-testid="settings-audit-log-search-risk-level"
              >
                {RISK_OPTIONS.map((opt) => (
                  <option key={opt.value || 'any'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.targetType}>目标类型</Label>
              <Input
                id={formIds.targetType}
                value={form.targetType}
                onChange={(e) =>
                  setForm((s) => ({ ...s, targetType: e.target.value }))
                }
                placeholder="例如 customer"
                data-testid="settings-audit-log-search-target-type"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.targetId}>目标 ID</Label>
              <Input
                id={formIds.targetId}
                value={form.targetId}
                onChange={(e) =>
                  setForm((s) => ({ ...s, targetId: e.target.value }))
                }
                placeholder="目标实体 id（不输出原文）"
                data-testid="settings-audit-log-search-target-id"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.approvalNo}>合规审批单号</Label>
              <Input
                id={formIds.approvalNo}
                value={form.approvalNo}
                onChange={(e) =>
                  setForm((s) => ({ ...s, approvalNo: e.target.value }))
                }
                placeholder="例如 APPR-2026-0001"
                data-testid="settings-audit-log-search-approval-no"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={formIds.actorId}>操作人 ID</Label>
              <Input
                id={formIds.actorId}
                value={form.actorId}
                onChange={(e) =>
                  setForm((s) => ({ ...s, actorId: e.target.value }))
                }
                placeholder="操作人 user_id"
                aria-describedby={actorWarn ? `${formIds.actorId}-warn` : undefined}
                data-testid="settings-audit-log-search-actor-id"
              />
              {actorWarn && (
                <p
                  id={`${formIds.actorId}-warn`}
                  className="text-xs text-amber-600"
                  data-testid="settings-audit-log-search-actor-self-warn"
                >
                  员工不可查询自己的操作审计；后端将拒绝该过滤。
                </p>
              )}
            </div>
            <div className="md:col-span-3 flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                data-testid="settings-audit-log-search-reset"
              >
                重置
              </Button>
              <Button
                type="submit"
                className="gap-2"
                disabled={view.list.loading}
                data-testid="settings-audit-log-search-submit"
              >
                <Search className="h-4 w-4" aria-hidden />
                搜索
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4" aria-hidden />
            聚合摘要
          </CardTitle>
          <CardDescription>
            来自 GET <code>/api/v1/audit-logs/summary</code>。
          </CardDescription>
        </CardHeader>
        <CardContent>{renderSummary(view.summary)}</CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">查询结果</CardTitle>
          <CardDescription>
            仅展示后端脱敏字段；任何原文不会出现在本表格中。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {renderList(view.list, view.rows)}
          {renderPagination(view)}
        </CardContent>
      </Card>

      {showExports && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-4 w-4" aria-hidden />
              审计导出任务（只读）
            </CardTitle>
            <CardDescription>
              来自 GET <code>/api/v1/audit-logs/exports</code>。
              本页面不提供导出创建入口：导出必须经由独立的二次验证工作流；
              前端不得 hardcode <code>secondFactorResult=passed</code>。
            </CardDescription>
          </CardHeader>
          <CardContent>{renderExports(view.exports)}</CardContent>
        </Card>
      )}
    </section>
  );
}

function renderSummary(state: AsyncState<AuditLogSummaryResponse>): ReactElement {
  if (state.loading && !state.data) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-audit-log-summary-loading"
      >
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        role="alert"
        data-testid="settings-audit-log-summary-error"
      >
        {state.error}
      </div>
    );
  }
  if (!state.data) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-audit-log-summary-empty"
      >
        暂无聚合数据。
      </div>
    );
  }
  return (
    <div
      className="space-y-3"
      data-testid="settings-audit-log-summary"
    >
      <dl className="grid gap-2 text-sm md:grid-cols-3">
        <Metric
          label="总条目"
          value={state.data.totalCount}
          testId="settings-audit-log-summary-total"
        />
        <Metric
          label="成功"
          value={state.data.successCount}
          testId="settings-audit-log-summary-success"
        />
        <Metric
          label="失败"
          value={state.data.failureCount}
          testId="settings-audit-log-summary-failure"
        />
      </dl>
      <div className="text-xs text-muted-foreground">
        聚合时间：{formatTimestamp(state.data.capturedAt)}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <BucketList
          title="Top 动作"
          items={state.data.topActions.map((b) => ({
            label: b.action,
            count: b.count,
          }))}
          testId="settings-audit-log-summary-top-actions"
          emptyMessage="无动作分布数据。"
        />
        <BucketList
          title="Top 资源"
          items={state.data.topResources.map((b) => ({
            label: b.resource,
            count: b.count,
          }))}
          testId="settings-audit-log-summary-top-resources"
          emptyMessage="无资源分布数据。"
        />
      </div>
    </div>
  );
}

function renderList(
  state: AsyncState<AuditLogListResponse>,
  rows: readonly AuditLogEntryDto[],
): ReactElement {
  if (state.loading && rows.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-audit-log-list-loading"
      >
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        role="alert"
        data-testid="settings-audit-log-list-error"
      >
        {state.error}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-audit-log-list-empty"
      >
        当前条件下没有匹配的审计记录。
      </div>
    );
  }
  return (
    <Table data-testid="settings-audit-log-list">
      <TableHeader>
        <TableRow>
          <TableHead>时间</TableHead>
          <TableHead>事件</TableHead>
          <TableHead>资源</TableHead>
          <TableHead>操作人</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>风险</TableHead>
          <TableHead>摘要</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.eventId}
            data-testid={`settings-audit-log-row-${row.eventId}`}
          >
            <TableCell className="whitespace-nowrap text-xs">
              {formatTimestamp(row.createdAt)}
            </TableCell>
            <TableCell className="text-xs">
              <div className="font-mono">{row.eventCode}</div>
              <div className="text-muted-foreground">
                {row.eventType}
                {row.action !== row.eventCode ? ` · ${row.action}` : ''}
              </div>
            </TableCell>
            <TableCell className="text-xs">
              <div className="font-mono">{row.resource}</div>
              <div className="text-muted-foreground">
                目标：{row.targetType ?? '—'}
                {row.targetIdMasked ? `（${row.targetIdMasked}）` : ''}
              </div>
              <div className="text-muted-foreground">
                资源 id：{row.resourceIdMasked ?? '—'}
              </div>
            </TableCell>
            <TableCell className="text-xs">
              <div className="font-mono">{row.actorId ?? '—'}</div>
              <div className="text-muted-foreground">
                角色：{row.actorRole ?? '—'}
              </div>
              <div className="text-muted-foreground">
                租户：{row.tenantIdMasked ?? '—'} / 机构：{row.orgIdMasked ?? '—'}
              </div>
            </TableCell>
            <TableCell className="text-xs">
              <span
                className={
                  row.status === 'success'
                    ? 'text-emerald-600'
                    : 'text-destructive'
                }
                data-testid={`settings-audit-log-row-${row.eventId}-status`}
              >
                {row.status === 'success' ? '成功' : '失败'}
              </span>
            </TableCell>
            <TableCell className="text-xs">
              {RISK_LEVEL_LABEL[row.riskLevel]}
            </TableCell>
            <TableCell className="text-xs">
              <div>{row.reasonSummary ?? '—'}</div>
              <div className="text-muted-foreground">
                敏感字段：{row.sensitiveSummary ?? '—'}
              </div>
              <div className="text-muted-foreground">
                IP：{row.ipSummary ?? '—'} · 设备：{row.deviceSummary ?? '—'}
              </div>
              <div className="text-muted-foreground">
                审批单号：{row.approvalNo ?? '—'} · 原文可见性：
                {RAW_PAYLOAD_VISIBILITY_LABEL[row.rawPayloadVisibility]}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function renderPagination(view: ReturnType<typeof useAuditLogs>): ReactElement {
  const total = view.list.data?.totalCount ?? 0;
  return (
    <div
      className="flex items-center justify-between text-xs text-muted-foreground"
      data-testid="settings-audit-log-pagination"
    >
      <div>
        第 {view.currentPageIndex + 1} 页 · 每页 {view.limit} 条 · 命中{' '}
        <span data-testid="settings-audit-log-total-count">{total}</span> 条
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={view.previousPage}
          disabled={!view.hasPreviousPage || view.list.loading}
          data-testid="settings-audit-log-prev-page"
        >
          上一页
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={view.nextPage}
          disabled={!view.hasNextPage || view.list.loading}
          data-testid="settings-audit-log-next-page"
        >
          下一页
        </Button>
      </div>
    </div>
  );
}

function renderExports(
  state: AsyncState<readonly AuditLogExportDto[]>,
): ReactElement {
  if (state.loading && !state.data) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-audit-log-exports-loading"
      >
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        role="alert"
        data-testid="settings-audit-log-exports-error"
      >
        {state.error}
      </div>
    );
  }
  if (!state.data || state.data.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-audit-log-exports-empty"
      >
        暂无审计导出任务。
      </div>
    );
  }
  return (
    <ul
      className="space-y-2 text-xs"
      data-testid="settings-audit-log-exports"
    >
      {state.data.map((task) => (
        <li
          key={task.id}
          className="rounded border p-2"
          data-testid={`settings-audit-log-export-${task.id}`}
        >
          <div className="flex items-center justify-between">
            <span className="font-mono">{task.fileLabel ?? task.id}</span>
            <span className="text-muted-foreground">
              {EXPORT_STATUS_LABEL[task.status]}
            </span>
          </div>
          <div className="mt-1 text-muted-foreground">
            申请人：{task.requestedByName ?? task.requestedBy}；
            行数：{task.rowCount ?? '—'}；
            下载剩余：{task.downloadLimit - task.downloadCount}/{task.downloadLimit}
          </div>
          <div className="text-muted-foreground">
            创建：{formatTimestamp(task.createdAt)}；
            下载窗口：
            {task.notBefore ? formatTimestamp(task.notBefore) : '—'}
            {' ~ '}
            {task.notAfter ? formatTimestamp(task.notAfter) : '—'}
          </div>
          <div className="text-muted-foreground">
            审批单号：{task.approvalNo} · 二次验证：
            {task.secondFactorPassed ? '已通过' : '未通过'}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Metric({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: number;
  readonly testId: string;
}): ReactElement {
  return (
    <div className="rounded border bg-muted/30 px-3 py-2">
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className="text-lg font-medium" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

function BucketList({
  title,
  items,
  testId,
  emptyMessage,
}: {
  readonly title: string;
  readonly items: readonly { label: string; count: number }[];
  readonly testId: string;
  readonly emptyMessage: string;
}): ReactElement {
  const sorted = useMemo(() => [...items].sort((a, b) => b.count - a.count), [items]);
  return (
    <div data-testid={testId}>
      <h4 className="mb-2 text-xs font-medium uppercase text-muted-foreground">
        {title}
      </h4>
      {sorted.length === 0 ? (
        <div className="text-xs text-muted-foreground">{emptyMessage}</div>
      ) : (
        <ul className="space-y-1 text-xs">
          {sorted.map((item) => (
            <li
              key={item.label}
              className="flex items-center justify-between rounded border bg-muted/20 px-2 py-1"
              data-testid={`${testId}-item-${item.label}`}
            >
              <span className="font-mono">{item.label}</span>
              <span>{item.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatTimestamp(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  return new Date(ts).toLocaleString();
}

export const DEFAULT_PAGE_SIZE = DEFAULT_AUDIT_LOG_PAGE_SIZE;
